import type { LinhaCompradorCalculada } from "@/api/metas.service";
import { avaliador, type Arvore, type Valores } from "./metasArvore";

/** Uma linha do relatório por comprador, no grão do `comprador_*.csv` do cliente: N1 × N2 × N3 × comprador, somado entre lojas. */
export interface LinhaRelatorioComprador {
  mesAno: number;
  departamento: string;
  secao: string;
  subsecao: string;
  comprador: string;
  metaFaturamento: number;
  metaContribuicao: number;
  metaPerda: number;
}

/**
 * Aproxima os ajustes manuais no relatório por comprador. O motor só tem o
 * calculado por produto; os ajustes existem na tela, no nível em que foram
 * feitos. Cada linha (loja × N3 × comprador) é escalada pela razão ajustada ÷
 * calculada do N3 daquela loja — o mesmo fator que a tela aplica na subárvore
 * —, o que reparte o ajuste do N3 entre os compradores na proporção do
 * calculado. Um ajuste feito direto num produto entra no N3 dele, mas diluído
 * entre os compradores do N3. N3 sem venda calculada fica sem escala. Lojas
 * fora de `lojas` (filtro da tela) não entram. A árvore precisa ter o N3 de
 * cada loja carregado.
 */
export function linhasRelatorioComprador(
  calculadas: LinhaCompradorCalculada[],
  arvore: Arvore,
  ajustes: Valores,
  lojas: number[],
  anoMeta: number,
  mesMeta: number,
): LinhaRelatorioComprador[] {
  const { nos } = arvore;
  const { ajustada } = avaliador(arvore, ajustes);
  const permitidas = new Set(lojas);
  const mesAno = anoMeta * 100 + mesMeta;
  const agregado = new Map<string, LinhaRelatorioComprador>();

  calculadas.forEach((linha) => {
    if (linha.codEmpresa === null || !permitidas.has(linha.codEmpresa)) return;
    const n3 = nos[linha.idEscala];
    const fator = n3 && n3.calculado ? ajustada(linha.idEscala) / n3.calculado : 1;
    const chave = [linha.departamento, linha.secao, linha.subsecao, linha.comprador].join("\u0000");
    const atual = agregado.get(chave) ?? {
      mesAno,
      departamento: linha.departamento,
      secao: linha.secao,
      subsecao: linha.subsecao,
      comprador: linha.comprador,
      metaFaturamento: 0,
      metaContribuicao: 0,
      metaPerda: 0,
    };
    atual.metaFaturamento += linha.venda * fator;
    atual.metaContribuicao += linha.contribuicao * fator;
    atual.metaPerda += linha.perda * fator;
    agregado.set(chave, atual);
  });

  const arredondar = (valor: number) => Math.round(valor * 100) / 100;
  return [...agregado.values()]
    .map((linha) => ({
      ...linha,
      metaFaturamento: arredondar(linha.metaFaturamento),
      metaContribuicao: arredondar(linha.metaContribuicao),
      metaPerda: arredondar(linha.metaPerda),
    }))
    .sort(
      (a, b) =>
        b.metaFaturamento - a.metaFaturamento ||
        a.departamento.localeCompare(b.departamento) ||
        a.secao.localeCompare(b.secao) ||
        a.subsecao.localeCompare(b.subsecao) ||
        a.comprador.localeCompare(b.comprador),
    );
}
